// janus-engine/traps/board-validators.ts — Lane TRAPS (2026-09-28): the three trap-disarm board validators, twins of
// includes/functions/inventory/trap_disarm_minigame_inc.php (ft_trap_minigame_validate_mechanical / _magical / _rune and
// ft_trap_minigame_board_of). PURE — the browser judges its own submission against the geometry the begin commit handed it (the
// same frozen geometry the legacy token held; no hidden information: it ships whole at init). The commit re-judges with PHP's
// own validator and refuses a disagreement (`verdict_mismatch`), the lockpick zone precedent.
// Parity: tools/test/janus-traps-validators.test.mjs against tools/test/fixtures/traps-validators.json (PHP-captured).
import { isPhpNumeric, phpIntval } from '../rolls/dice-roll.js';

export type TrapBoard = 'rune' | 'weave' | 'mechanical';
export interface WeaveThread { a1: number; a2: number; depth: number }
export interface WeaveReplay { complete: boolean; wrong_cuts: number; warning_used: boolean }

const rec = (v: unknown): Record<string, unknown> | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
/** PHP (float) of an is_numeric() value. */
const phpFloat = (v: unknown): number => typeof v === 'number' ? v : typeof v === 'boolean' ? (v ? 1 : 0) : Number(String(v ?? '').trim());
/** PHP isset($arr[$key]): the key exists and is not null. */
const isset = (row: Record<string, unknown> | null, key: string): boolean => !!row && row[key] !== undefined && row[key] !== null;

/** ft_trap_minigame_board_of(): branch on the GEOMETRY's shape, never on the classification (a pre-rune-swap token carries a weave). */
export function trapBoardOf(geometry: unknown): TrapBoard {
  const g = rec(geometry);
  if (g?.board === 'rune' || isset(g, 'runes')) return 'rune';
  if (isset(g, 'threads')) return 'weave';
  return 'mechanical';
}

/** ft_trap_minigame_validate_mechanical(): every submitted sear position sits inside its catch zone; the count must match exactly. */
export function validateMechanical(geometry: unknown, positions: unknown): boolean {
  const g = rec(geometry);
  const sears = Array.isArray(g?.sears) ? g!.sears as unknown[] : [];
  const list = Array.isArray(positions) ? positions : [];
  if (list.length !== sears.length) return false;
  for (let i = 0; i < sears.length; i++) {
    const sear = rec(sears[i]) ?? {};
    const pos = list[i];
    if (pos === undefined || pos === null || !isPhpNumeric(pos)) return false;
    const p = phpFloat(pos);
    if (p < phpFloat(sear.zone_lo) || p > phpFloat(sear.zone_hi)) return false;
  }
  return true;
}

/** ft_trap_minigame_chords_cross(): exactly one endpoint of B lies strictly inside A's counter-clockwise arc. */
export function chordsCross(t1: WeaveThread, t2: WeaveThread): boolean {
  const norm = (a: number): number => ((a % 360) + 360) % 360;
  const a1 = norm(Number(t1.a1)), a2 = norm(Number(t1.a2));
  const span = norm(a2 - a1);
  if (span <= 0) return false;
  let inside = 0;
  for (const b of [Number(t2.a1), Number(t2.a2)]) {
    const d = norm(b - a1);
    if (d > 0 && d < span) inside++;
  }
  return inside === 1;
}

/** PHP `!is_numeric($raw) || (int) $raw != $raw` — an integer-valued index only ("1.9" never aliases thread 1). */
function cutIndex(raw: unknown): number | null {
  if (typeof raw === 'boolean' || raw === null || raw === undefined || !isPhpNumeric(raw)) return null;
  const asInt = phpIntval(raw);
  return asInt === phpFloat(raw) ? asInt : null;
}

/**
 * ft_trap_minigame_validate_magical() (the Glyph Unweaving weave — a token minted before the 2026-08-05 rune swap): replay the cut
 * order. A thread is cuttable only while no uncut thread with a higher depth crosses it; the first illegal cut re-knits it and brings
 * the reserve thread into play; the second is a discharge (the caller treats `wrong_cuts >= 2` as sprung).
 */
export function validateMagical(geometry: unknown, cutSequence: unknown): WeaveReplay {
  const g = rec(geometry);
  const threads: WeaveThread[] = (Array.isArray(g?.threads) ? g!.threads as unknown[] : []).map(t => rec(t) as unknown as WeaveThread);
  const reserveIndex = threads.length;
  threads.push((rec(g?.reserve) as unknown as WeaveThread) ?? { a1: 0, a2: 180, depth: reserveIndex });
  // PHP: array_fill_keys(range(0, count - 2)) — the reserve starts OUT of play. A geometry without main threads is never minted.
  const uncut = new Set<number>();
  for (let i = 0; i < reserveIndex; i++) uncut.add(i);
  let reserveActive = false;
  let wrongCuts = 0;
  const seq = Array.isArray(cutSequence) ? cutSequence : [];
  for (const raw of seq) {
    const idx = cutIndex(raw);
    if (idx === null || !uncut.has(idx) || (idx === reserveIndex && !reserveActive)) return { complete: false, wrong_cuts: wrongCuts + 1, warning_used: reserveActive };
    let legal = true;
    for (const other of uncut) {
      if (other === idx) continue;
      if (Number(threads[other]!.depth) > Number(threads[idx]!.depth) && chordsCross(threads[other]!, threads[idx]!)) { legal = false; break; }
    }
    if (legal) { uncut.delete(idx); continue; }
    wrongCuts++;
    if (wrongCuts >= 2) return { complete: false, wrong_cuts: wrongCuts, warning_used: reserveActive };
    if (!reserveActive) { reserveActive = true; uncut.add(reserveIndex); }
  }
  return { complete: uncut.size === 0, wrong_cuts: wrongCuts, warning_used: reserveActive };
}

/** ft_trap_minigame_validate_rune(): the completed-rune count equals what was minted; an empty rune list NEVER passes vacuously. */
export function validateRune(geometry: unknown, runesCompleted: unknown): boolean {
  const runes = rec(geometry)?.runes;
  if (!Array.isArray(runes) || runes.length === 0) return false;
  if (typeof runesCompleted === 'boolean' || runesCompleted === null || runesCompleted === undefined || !isPhpNumeric(runesCompleted)) return false;
  return phpIntval(runesCompleted) === runes.length;
}

/** The submission each board sends (the shell's TrapDisarmResultPayload), judged with disarm_result_inc.php's exact band rules. */
export interface DisarmSubmission {
  sear_positions?: unknown; never_left_green?: unknown; cut_sequence?: unknown; clean_strokes?: unknown; no_pulse_violations?: unknown; runes_completed?: unknown;
}
export interface DisarmVerdict { board: TrapBoard; success: boolean; sprung: boolean; perfect: boolean }
/** PHP !empty() of a posted flag (true / 1 / "1" / non-empty non-"0" string). */
const phpNotEmpty = (v: unknown): boolean => !(v === undefined || v === null || v === false || v === 0 || v === '' || v === '0' || (Array.isArray(v) && v.length === 0));
/** disarm_result_inc.php:75-106 — success / sprung (weave: 2+ wrong cuts) / perfect, on the minted board's shape. */
export function judgeDisarm(geometry: unknown, submission: DisarmSubmission): DisarmVerdict {
  const board = trapBoardOf(geometry);
  if (board === 'rune') {
    const success = validateRune(geometry, submission.runes_completed ?? null);
    return { board, success, sprung: false, perfect: success && phpNotEmpty(submission.never_left_green) };
  }
  if (board === 'weave') {
    const replay = validateMagical(geometry, Array.isArray(submission.cut_sequence) ? submission.cut_sequence : []);
    const success = replay.complete;
    return { board, success, sprung: !success && replay.wrong_cuts >= 2,
      perfect: success && replay.wrong_cuts === 0 && !replay.warning_used && phpNotEmpty(submission.clean_strokes) && phpNotEmpty(submission.no_pulse_violations) };
  }
  const success = validateMechanical(geometry, Array.isArray(submission.sear_positions) ? submission.sear_positions : []);
  return { board, success, sprung: false, perfect: success && phpNotEmpty(submission.never_left_green) };
}
